import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { TourProvider as ReactourProvider, useTour as useReactour } from '@reactour/tour';
import { useAuth } from './AuthContext';
import { useLocation } from 'react-router-dom';

const TourContext = createContext();

export const useTour = () => {
  const context = useContext(TourContext);
  if (!context) {
    throw new Error('useTour must be used within a TourProvider');
  }
  return context;
};

// Inner component that has access to Reactour's useTour hook
const TourController = ({ children }) => {
  const { user } = useAuth();
  const location = useLocation();
  const { setIsOpen, setCurrentStep, currentStep, setSteps } = useReactour();
  const [tourCompleted, setTourCompleted] = useState(false);
  const [showResumeDialog, setShowResumeDialog] = useState(false);
  const [resumeStep, setResumeStep] = useState(0);
  const [isTourActive, setIsTourActive] = useState(false);

  // Define tour steps based on user role
  const getTourSteps = useCallback(() => {
    const isInstructor = user?.role === 'Instructor' || user?.role === 'TenantAdmin' || user?.role === 'SuperAdmin';
    const isAdmin = user?.role === 'TenantAdmin' || user?.role === 'SuperAdmin';
    const isSuperAdmin = user?.role === 'SuperAdmin';
    
    const commonSteps = [
      {
        selector: '[data-testid="nav-courses"]',
        content: (
          <div>
            <h3 className="text-lg font-bold mb-2">📚 Courses</h3>
            <p>Access all your courses here. Browse enrolled courses, view progress, and access course content, labs, and assignments.</p>
          </div>
        ),
      },
      {
        selector: '[data-testid="nav-ai-labs"]',
        content: (
          <div>
            <h3 className="text-lg font-bold mb-2">🔬 AI Labs</h3>
            <p>Interactive ML/AI workflow builder. Create, run, and learn from hands-on machine learning experiments with drag-and-drop blocks.</p>
            <p className="mt-2 text-sm text-gray-600"><strong>New:</strong> Visual execution feedback shows node progress with color-coded states!</p>
          </div>
        ),
      },
      {
        selector: '[data-testid="nav-help"]',
        content: (
          <div>
            <h3 className="text-lg font-bold mb-2">❓ Help & Support</h3>
            <p>Find documentation, tutorials, and FAQs. Submit support tickets and restart this tour anytime.</p>
          </div>
        ),
      },
    ];

    const instructorSteps = [
      {
        selector: '[data-testid="nav-content-builder"]',
        content: (
          <div>
            <h3 className="text-lg font-bold mb-2">✨ Course Blueprint</h3>
            <p>AI-powered course creation tool. Define your course, select a pedagogy template, and let AI generate topics, labs, and assignments.</p>
            <p className="mt-2 text-sm text-gray-600">New: Customize pedagogy templates with topic count rules!</p>
          </div>
        ),
      },
      {
        selector: '[data-testid="nav-analytics"]',
        content: (
          <div>
            <h3 className="text-lg font-bold mb-2">📊 Analytics</h3>
            <p>Track learner progress, identify at-risk students, and view engagement metrics. Get actionable insights to improve learning outcomes.</p>
            <p className="mt-2 text-sm text-gray-600">Features: Progress funnel, at-risk detection, topic performance analysis.</p>
          </div>
        ),
      },
    ];

    const adminSteps = [
      {
        selector: '[data-testid="nav-organization"]',
        content: (
          <div>
            <h3 className="text-lg font-bold mb-2">🏢 Organization</h3>
            <p>Manage your organization settings, branding, users, and features.</p>
            <ul className="mt-2 text-sm text-gray-600 list-disc pl-4">
              <li>User Management: Add/manage users and cohorts</li>
              <li>Settings: Branding, logos, and colors</li>
              <li>Admin Console: Dashboard, audit logs, support</li>
              <li>SSO: Configure enterprise authentication</li>
            </ul>
          </div>
        ),
      },
    ];

    const superAdminSteps = [
      {
        selector: '[data-testid="nav-organizations"]',
        content: (
          <div>
            <h3 className="text-lg font-bold mb-2">🌐 Organizations</h3>
            <p>Multi-tenant management dashboard for Super Admins.</p>
            <ul className="mt-2 text-sm text-gray-600 list-disc pl-4">
              <li>Manage all tenant organizations</li>
              <li>System health monitoring (CPU, Memory, DB)</li>
              <li>Platform-wide analytics and user growth</li>
              <li>System announcements</li>
            </ul>
          </div>
        ),
      },
    ];

    let steps = [...commonSteps];
    
    if (isInstructor) {
      steps = [...instructorSteps, ...steps];
    }
    
    if (isAdmin) {
      steps = [...steps, ...adminSteps];
    }
    
    if (isSuperAdmin) {
      steps = [...steps, ...superAdminSteps];
    }
    
    // Add final welcome step
    steps.push({
      selector: '[data-testid="user-avatar"]',
      content: (
        <div>
          <h3 className="text-lg font-bold mb-2">👤 Your Profile</h3>
          <p>Access your account settings, notification preferences, and logout from here.</p>
          <p className="mt-2 text-sm text-gray-600">Click the bell icon 🔔 for real-time notifications.</p>
          <p className="mt-2 font-medium text-orange-600">🎉 {"You're all set! Explore and enjoy Elevante.AI!"}</p>
        </div>
      ),
    });
    
    return steps;
  }, [user]);

  // Check if user has completed the tour or has preferences
  useEffect(() => {
    if (user) {
      const completed = localStorage.getItem(`tour_completed_${user.id}`);
      const dontAskAgain = localStorage.getItem(`tour_dont_ask_${user.id}`);
      const savedStep = localStorage.getItem(`tour_step_${user.id}`);
      
      // Use ref to track completion state without causing re-render loops
      const isCompleted = completed === 'true';
      if (isCompleted !== tourCompleted) {
        // Use setTimeout to avoid synchronous setState in effect
        setTimeout(() => setTourCompleted(isCompleted), 0);
      }
      
      // Update steps when user changes
      setSteps(getTourSteps());
      
      // If tour was interrupted and user didn't choose "don't ask again"
      if (savedStep && savedStep !== '0' && dontAskAgain !== 'true' && !completed) {
        // Use setTimeout to avoid synchronous setState in effect
        setTimeout(() => {
          setResumeStep(parseInt(savedStep, 10));
          // Show resume dialog after a short delay
          setTimeout(() => {
            setShowResumeDialog(true);
          }, 1500);
        }, 0);
      }
    }
  }, [user, location.pathname, setSteps, getTourSteps, tourCompleted]);

  // Save current step when it changes
  useEffect(() => {
    if (user && isTourActive) {
      localStorage.setItem(`tour_step_${user.id}`, currentStep.toString());
    }
  }, [currentStep, user, isTourActive]);

  const closeTour = useCallback(() => {
    setIsOpen(false);
    setIsTourActive(false);
    if (user) {
      localStorage.setItem(`tour_completed_${user.id}`, 'true');
      localStorage.removeItem(`tour_step_${user.id}`);
      setTourCompleted(true);
    }
  }, [user, setIsOpen]);

  // Handle tour abort (user clicked X or outside)
  const handleTourAbort = useCallback(() => {
    setIsOpen(false);
    setIsTourActive(false);
    // Show the resume preference dialog
    setTimeout(() => {
      setShowResumeDialog(true);
    }, 300);
  }, [setIsOpen]);

  // User chose to be reminded later
  const handleRemindLater = useCallback(() => {
    setShowResumeDialog(false);
    // Step is already saved in localStorage
  }, []);

  // User chose don't ask again
  const handleDontAskAgain = useCallback(() => {
    setShowResumeDialog(false);
    if (user) {
      localStorage.setItem(`tour_dont_ask_${user.id}`, 'true');
      localStorage.removeItem(`tour_step_${user.id}`);
    }
  }, [user]);

  // Resume tour from saved step
  const handleResumeTour = useCallback(() => {
    setShowResumeDialog(false);
    setCurrentStep(resumeStep);
    setIsTourActive(true);
    setIsOpen(true);
  }, [resumeStep, setCurrentStep, setIsOpen]);

  // Start fresh tour
  const handleStartFresh = useCallback(() => {
    setShowResumeDialog(false);
    setCurrentStep(0);
    if (user) {
      localStorage.removeItem(`tour_step_${user.id}`);
    }
    setIsTourActive(true);
    setIsOpen(true);
  }, [user, setCurrentStep, setIsOpen]);

  const startTour = useCallback(() => {
    setCurrentStep(0);
    setSteps(getTourSteps());
    setIsTourActive(true);
    setIsOpen(true);
  }, [setCurrentStep, setIsOpen, setSteps, getTourSteps]);

  const resetTour = useCallback(() => {
    if (user) {
      localStorage.removeItem(`tour_completed_${user.id}`);
      localStorage.removeItem(`tour_dont_ask_${user.id}`);
      localStorage.removeItem(`tour_step_${user.id}`);
      setTourCompleted(false);
    }
    startTour();
  }, [user, startTour]);

  const steps = getTourSteps();

  return (
    <TourContext.Provider value={{ startTour, resetTour, tourCompleted, closeTour, handleTourAbort }}>
      {children}
      
      {/* Resume Tour Dialog */}
      {showResumeDialog && user && (
        <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full mx-4 overflow-hidden">
            <div className="bg-gradient-to-r from-orange-500 to-amber-500 px-6 py-4">
              <h3 className="text-white text-lg font-bold flex items-center gap-2">
                🎯 Continue Your Tour?
              </h3>
            </div>
            <div className="p-6">
              {resumeStep > 0 ? (
                <p className="text-gray-600 mb-4">
                  You were on step {resumeStep + 1} of {steps.length}. Would you like to continue where you left off?
                </p>
              ) : (
                <p className="text-gray-600 mb-4">
                  Would you like to take a quick tour of the platform features?
                </p>
              )}
              
              <div className="space-y-2">
                {resumeStep > 0 && (
                  <button
                    onClick={handleResumeTour}
                    className="w-full px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors font-medium"
                  >
                    Continue Tour (Step {resumeStep + 1}/{steps.length})
                  </button>
                )}
                <button
                  onClick={handleStartFresh}
                  className={`w-full px-4 py-2 ${resumeStep > 0 ? 'bg-gray-100 text-gray-700 hover:bg-gray-200' : 'bg-orange-500 text-white hover:bg-orange-600'} rounded-lg transition-colors font-medium`}
                >
                  {resumeStep > 0 ? 'Start From Beginning' : 'Start Tour'}
                </button>
                <button
                  onClick={handleRemindLater}
                  className="w-full px-4 py-2 text-gray-500 hover:text-gray-700 transition-colors text-sm"
                >
                  Remind Me Later
                </button>
                <button
                  onClick={handleDontAskAgain}
                  className="w-full px-4 py-2 text-gray-400 hover:text-gray-600 transition-colors text-xs"
                >
                  {"Don't Ask Again"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </TourContext.Provider>
  );
};

// Main TourProvider that wraps ReactourProvider
export const TourProvider = ({ children }) => {
  const { user } = useAuth();
  
  // Default empty steps - will be set by TourController
  const defaultSteps = [];

  // Custom styles for the tour
  const styles = {
    popover: (base) => ({
      ...base,
      borderRadius: '8px',
      padding: '16px',
    }),
    maskArea: (base) => ({
      ...base,
      rx: 8,
    }),
    badge: (base) => ({
      ...base,
      backgroundColor: '#f97316',
    }),
    close: (base) => ({
      ...base,
      color: '#666',
    }),
  };

  if (!user) {
    return <TourContext.Provider value={{ startTour: () => {}, resetTour: () => {}, tourCompleted: false }}>{children}</TourContext.Provider>;
  }

  return (
    <ReactourProvider
      steps={defaultSteps}
      styles={styles}
      showBadge={false}
      showNavigation={true}
      showDots={true}
      scrollSmooth={true}
      padding={{ mask: 10, popover: [5, 10] }}
    >
      <TourController>
        {children}
      </TourController>
    </ReactourProvider>
  );
};
